import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  AddOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ExpandMoreOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChoiceControl,
  createFocusRingStyles,
  designTokens,
  EmptyState,
  FeedbackSnackbar,
  FormTextField,
  ResourceState,
  SectionCard,
  ValueBadge,
} from '../../../../design-system';
import { WorkflowDurationField } from '../../components/WorkflowDurationField';
import { workflowApprovalLabelBadgeTone, workflowApprovalLabelToneLabel } from '../../workflowApprovalLabelPresentation';
import {
  groupWorkflowApprovalSteps,
  insertWorkflowApprovalStepInStage,
  mergeWorkflowApprovalStageWithPrevious,
  moveWorkflowApprovalStage,
  normalizeWorkflowApprovalSteps,
  splitWorkflowApprovalStageAt,
  workflowApprovalStageSequenceIsValid,
} from '../../workflowApprovalStages';
import type {
  WorkflowApprovalLabel,
  WorkflowApprovalLabelInput,
  WorkflowApprovalLabelTone,
  WorkflowApprovalResolverType,
  WorkflowApprovalSettingsDeleteTarget,
  WorkflowApprovalSettingsView,
  WorkflowApprovalTemplate,
  WorkflowApprovalTemplateInput,
  WorkflowApprovalTemplateStep,
} from '../model';

type ReusableApprovalResolverType = Exclude<
  WorkflowApprovalResolverType,
  'SUBJECT_ORG_LEADER' | 'FORM_PERSON_FIELD'
>;

type ResourceProps = {
  loading?: boolean;
  error?: string;
  readOnly?: boolean;
  onRetry?: () => void;
};

export type WorkflowSettingsFixedPersonPickerRendererProps = {
  personID: string;
  disabled: boolean;
  onChange: (personID: string) => void;
};

export type WorkflowSettingsFixedRolePickerRendererProps = {
  roleCode: string;
  disabled: boolean;
  onChange: (roleCode: string) => void;
};

export type ApprovalTemplatesPanelProps = ResourceProps & {
  templates: readonly WorkflowApprovalTemplate[];
  labels: readonly WorkflowApprovalLabel[];
  onCreate?: (input: WorkflowApprovalTemplateInput) => Promise<WorkflowApprovalTemplate>;
  onUpdate?: (id: string, input: WorkflowApprovalTemplateInput) => Promise<WorkflowApprovalTemplate>;
  onDelete?: (id: string, expectedVersion: number) => Promise<void>;
  confirmDelete?: (target: WorkflowApprovalSettingsDeleteTarget) => boolean | Promise<boolean>;
  confirmDiscardChanges?: (name: string) => boolean | Promise<boolean>;
  renderFixedPersonPicker?: (props: WorkflowSettingsFixedPersonPickerRendererProps) => ReactNode;
  renderFixedRolePicker?: (props: WorkflowSettingsFixedRolePickerRendererProps) => ReactNode;
};

export type ApprovalLabelsPanelProps = ResourceProps & {
  labels: readonly WorkflowApprovalLabel[];
  onCreate?: (input: WorkflowApprovalLabelInput) => Promise<WorkflowApprovalLabel>;
  onUpdate?: (id: string, input: WorkflowApprovalLabelInput) => Promise<WorkflowApprovalLabel>;
  onDelete?: (id: string, expectedVersion: number) => Promise<void>;
  confirmDelete?: (target: WorkflowApprovalSettingsDeleteTarget) => boolean | Promise<boolean>;
  confirmDiscardChanges?: (name: string) => boolean | Promise<boolean>;
};

export type WorkflowApprovalSettingsUIProps = ResourceProps & {
  view: WorkflowApprovalSettingsView;
  templates: readonly WorkflowApprovalTemplate[];
  labels: readonly WorkflowApprovalLabel[];
  onCreateTemplate?: ApprovalTemplatesPanelProps['onCreate'];
  onUpdateTemplate?: ApprovalTemplatesPanelProps['onUpdate'];
  onDeleteTemplate?: ApprovalTemplatesPanelProps['onDelete'];
  onCreateLabel?: ApprovalLabelsPanelProps['onCreate'];
  onUpdateLabel?: ApprovalLabelsPanelProps['onUpdate'];
  onDeleteLabel?: ApprovalLabelsPanelProps['onDelete'];
  renderFixedPersonPicker?: ApprovalTemplatesPanelProps['renderFixedPersonPicker'];
  renderFixedRolePicker?: ApprovalTemplatesPanelProps['renderFixedRolePicker'];
  confirmDelete?: (target: WorkflowApprovalSettingsDeleteTarget) => boolean | Promise<boolean>;
  confirmDiscardChanges?: (name: string) => boolean | Promise<boolean>;
};

type TemplateEditorDraft = WorkflowApprovalTemplateInput & {
  id?: string;
  version?: number;
};

type LabelEditorDraft = WorkflowApprovalLabelInput & {
  id?: string;
  version?: number;
};

type MutationKind = 'create' | 'update' | 'delete';

const resolverPresentation: Record<ReusableApprovalResolverType, string> = {
  REQUESTER_ORG_LEADER: '요청자의 조직장',
  ROOT_ORG_LEADER: '공식 최상위 조직장',
  FIXED_PERSON: '특정 구성원',
  FIXED_ROLE: '특정 역할',
};

export function WorkflowApprovalSettingsUI({
  view,
  templates,
  labels,
  loading,
  error,
  readOnly,
  onRetry,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  renderFixedPersonPicker,
  renderFixedRolePicker,
  confirmDelete,
  confirmDiscardChanges,
}: WorkflowApprovalSettingsUIProps) {
  if (view === 'templates') {
    return <ApprovalTemplatesPanel
      templates={templates}
      labels={labels}
      loading={loading}
      error={error}
      readOnly={readOnly}
      onRetry={onRetry}
      onCreate={onCreateTemplate}
      onUpdate={onUpdateTemplate}
      onDelete={onDeleteTemplate}
      renderFixedPersonPicker={renderFixedPersonPicker}
      renderFixedRolePicker={renderFixedRolePicker}
      confirmDelete={confirmDelete}
      confirmDiscardChanges={confirmDiscardChanges}
    />;
  }

  return <ApprovalLabelsPanel
    labels={labels}
    loading={loading}
    error={error}
    readOnly={readOnly}
    onRetry={onRetry}
    onCreate={onCreateLabel}
    onUpdate={onUpdateLabel}
    onDelete={onDeleteLabel}
    confirmDelete={confirmDelete}
    confirmDiscardChanges={confirmDiscardChanges}
  />;
}

export function ApprovalTemplatesPanel({
  templates,
  labels,
  loading = false,
  error,
  readOnly = false,
  onRetry,
  onCreate,
  onUpdate,
  onDelete,
  renderFixedPersonPicker,
  renderFixedRolePicker,
  confirmDelete,
  confirmDiscardChanges,
}: ApprovalTemplatesPanelProps) {
  const editorTitleId = useId();
  const [confirmedTemplates, setConfirmedTemplates] = useState<Record<string, WorkflowApprovalTemplate>>({});
  const [confirmedDeletedIds, setConfirmedDeletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const effectiveTemplates = useMemo(
    () => mergeConfirmedRecords(templates, confirmedTemplates, confirmedDeletedIds),
    [confirmedDeletedIds, confirmedTemplates, templates],
  );
  const [selectedId, setSelectedId] = useState<string>();
  const [draft, setDraft] = useState<TemplateEditorDraft>();
  const [baseline, setBaseline] = useState('');
  const [creating, setCreating] = useState(false);
  const [mutation, setMutation] = useState<MutationKind>();
  const [mutationError, setMutationError] = useState('');
  const [notice, setNotice] = useState('');
  const dirty = draft !== undefined && serializeTemplateDraft(draft) !== baseline;
  const canCreate = !readOnly && Boolean(onCreate);
  const canUpdate = !readOnly && Boolean(onUpdate);
  const canDelete = !readOnly && Boolean(onDelete && confirmDelete);
  const busy = mutation !== undefined;

  useEffect(() => {
    if (creating || busy || dirty) return;
    const selected = effectiveTemplates.find((item) => item.id === selectedId);
    if (selected) {
      if (!draft || draft.id !== selected.id || draft.version !== selected.version) {
        loadTemplateDraft(selected, setDraft, setBaseline);
      }
      return;
    }
    const first = effectiveTemplates[0];
    if (first) {
      setSelectedId(first.id);
      loadTemplateDraft(first, setDraft, setBaseline);
    } else if (!creating) {
      setSelectedId(undefined);
      setDraft(undefined);
      setBaseline('');
    }
  }, [busy, creating, dirty, draft, effectiveTemplates, selectedId]);

  const selectTemplate = async (template: WorkflowApprovalTemplate) => {
    if (template.id === selectedId && !creating) return;
    if (!await changesCanBeDiscarded(dirty, draft?.name, confirmDiscardChanges)) return;
    setCreating(false);
    setSelectedId(template.id);
    setMutationError('');
    setNotice('');
    loadTemplateDraft(template, setDraft, setBaseline);
  };

  const startCreate = async () => {
    if (!canCreate || !await changesCanBeDiscarded(dirty, draft?.name, confirmDiscardChanges)) return;
    const next = newTemplateDraft();
    setCreating(true);
    setSelectedId(undefined);
    setDraft(next);
    setBaseline(serializeTemplateDraft(next));
    setMutationError('');
    setNotice('');
  };

  const resetDraft = () => {
    if (!draft || busy) return;
    if (creating) {
      const next = newTemplateDraft();
      setDraft(next);
      setBaseline(serializeTemplateDraft(next));
      return;
    }
    const source = effectiveTemplates.find((item) => item.id === draft.id);
    if (source) loadTemplateDraft(source, setDraft, setBaseline);
  };

  const saveTemplate = async () => {
    if (!draft || busy) return;
    const validationError = templateValidationError(draft);
    if (validationError) {
      setMutationError(validationError);
      return;
    }
    const input: WorkflowApprovalTemplateInput = {
      name: draft.name.trim(),
      description: optionalTrimmed(draft.description),
      approvalSteps: reorderSteps(draft.approvalSteps).map(cloneStep),
      expectedVersion: draft.id ? draft.version : undefined,
    };
    setMutation(draft.id ? 'update' : 'create');
    setMutationError('');
    setNotice('');
    try {
      const saved = draft.id
        ? await requireCallback(onUpdate, '승인 템플릿 수정 기능이 연결되지 않았습니다.')(draft.id, input)
        : await requireCallback(onCreate, '승인 템플릿 생성 기능이 연결되지 않았습니다.')(input);
      setConfirmedTemplates((current) => ({ ...current, [saved.id]: saved }));
      setConfirmedDeletedIds((current) => withoutSetValue(current, saved.id));
      setCreating(false);
      setSelectedId(saved.id);
      loadTemplateDraft(saved, setDraft, setBaseline);
      setNotice(`“${saved.name}” 승인 템플릿을 저장했습니다.`);
    } catch (reason) {
      setMutationError(errorMessage(reason, '승인 템플릿을 저장하지 못했습니다.'));
    } finally {
      setMutation(undefined);
    }
  };

  const deleteTemplate = async () => {
    if (!draft?.id || draft.version === undefined || !onDelete || !confirmDelete || busy) return;
    const target: WorkflowApprovalSettingsDeleteTarget = {
      kind: 'template', id: draft.id, name: draft.name, version: draft.version,
    };
    if (!await confirmDelete(target)) return;
    setMutation('delete');
    setMutationError('');
    setNotice('');
    try {
      await onDelete(target.id, target.version);
      const remaining = effectiveTemplates.filter((item) => item.id !== target.id);
      setConfirmedDeletedIds((current) => withSetValue(current, target.id));
      setConfirmedTemplates((current) => omitRecordKey(current, target.id));
      setCreating(false);
      if (remaining[0]) {
        setSelectedId(remaining[0].id);
        loadTemplateDraft(remaining[0], setDraft, setBaseline);
      } else {
        setSelectedId(undefined);
        setDraft(undefined);
        setBaseline('');
      }
      setNotice(`“${target.name}” 승인 템플릿을 보관했습니다.`);
    } catch (reason) {
      setMutationError(errorMessage(reason, '승인 템플릿을 보관하지 못했습니다.'));
    } finally {
      setMutation(undefined);
    }
  };

  const updateDraft = (patch: Partial<TemplateEditorDraft>) => {
    setDraft((current) => current ? { ...current, ...patch } : current);
    setMutationError('');
    setNotice('');
  };

  return <ResourceState
    loading={loading}
    error={error}
    empty={false}
    onRetry={onRetry}
    loadingLabel="승인 템플릿을 불러오는 중입니다."
  >
    <Stack spacing={1.5}>
      {readOnly ? <Alert severity="info">조회 전용 상태입니다. 승인 템플릿을 확인할 수 있지만 변경할 수는 없습니다.</Alert> : null}
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice('')} />
      {mutationError ? <Alert severity="error" onClose={() => setMutationError('')}>{mutationError}</Alert> : null}
      <SettingsSplitLayout
        list={<SettingsListCard
          title="승인 템플릿"
          description="자주 사용하는 승인 단계를 저장해 워크플로우 양식과 요청에 재사용합니다."
          action={canCreate ? <Button size="small" startIcon={<AddOutlined />} onClick={() => void startCreate()} disabled={busy}>새 템플릿</Button> : undefined}
          empty={effectiveTemplates.length === 0}
          emptyMessage={readOnly ? '등록된 승인 템플릿이 없습니다.' : '등록된 템플릿이 없습니다. 새 템플릿을 만들어 보세요.'}
        >
          {effectiveTemplates.map((template) => <SettingsListButton
            key={template.id}
            selected={!creating && selectedId === template.id}
            ariaLabel={`${template.name} 승인 템플릿 편집`}
            onClick={() => void selectTemplate(template)}
            disabled={busy}
            title={template.name}
            description={template.description || `${template.approvalSteps.length}개 승인 단계`}
            meta={`v${template.version} · ${template.approvalSteps.length}단계`}
          />)}
        </SettingsListCard>}
        editor={draft ? <Box component="section" aria-labelledby={editorTitleId} sx={{ minWidth: 0 }}>
          <SectionCard
            title={<Typography id={editorTitleId} component="h3" variant="h6">{creating ? '새 승인 템플릿' : '승인 템플릿 편집'}</Typography>}
            bodyPadding="standard"
            action={readOnly ? undefined : <EditorActions
              dirty={dirty}
              busy={busy}
              saving={mutation === 'create' || mutation === 'update'}
              canSave={creating ? canCreate : canUpdate}
              onReset={resetDraft}
              onSave={() => void saveTemplate()}
            />}
          >
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormTextField
                  required
                  label="템플릿 이름"
                  value={draft.name}
                  disabled={readOnly || busy}
                  onChange={(event) => updateDraft({ name: event.target.value })}
                  slotProps={{ htmlInput: { maxLength: 120 } }}
                  sx={{ flex: 1 }}
                />
                {!creating ? <FormTextField label="버전" value={`v${draft.version ?? 1}`} disabled sx={{ width: { sm: 112 } }} /> : null}
              </Stack>
              <FormTextField
                label="설명"
                value={draft.description ?? ''}
                disabled={readOnly || busy}
                onChange={(event) => updateDraft({ description: event.target.value })}
                multiline
                minRows={2}
                characterCount={{ current: (draft.description ?? '').length, max: 500 }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <ApprovalStepsEditor
                value={draft.approvalSteps}
                labels={labels}
                disabled={readOnly || busy}
                renderFixedPersonPicker={renderFixedPersonPicker}
                renderFixedRolePicker={renderFixedRolePicker}
                onChange={(approvalSteps) => updateDraft({ approvalSteps })}
              />
              {!creating && canDelete && draft.id ? <>
                <Divider />
                <EditorArchiveAction busy={busy} deleting={mutation === 'delete'} onDelete={() => void deleteTemplate()} />
              </> : null}
            </Stack>
          </SectionCard>
        </Box> : <EditorEmptyState
          title="승인 템플릿을 선택하세요."
          description={readOnly ? '왼쪽 목록에서 확인할 템플릿을 선택하세요.' : '기존 템플릿을 선택하거나 새 템플릿을 만드세요.'}
          action={canCreate ? <Button variant="contained" startIcon={<AddOutlined />} onClick={() => void startCreate()}>새 템플릿</Button> : undefined}
        />}
      />
    </Stack>
  </ResourceState>;
}

export function ApprovalLabelsPanel({
  labels,
  loading = false,
  error,
  readOnly = false,
  onRetry,
  onCreate,
  onUpdate,
  onDelete,
  confirmDelete,
  confirmDiscardChanges,
}: ApprovalLabelsPanelProps) {
  const editorTitleId = useId();
  const [confirmedLabels, setConfirmedLabels] = useState<Record<string, WorkflowApprovalLabel>>({});
  const [confirmedDeletedIds, setConfirmedDeletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const effectiveLabels = useMemo(
    () => mergeConfirmedRecords(labels, confirmedLabels, confirmedDeletedIds),
    [confirmedDeletedIds, confirmedLabels, labels],
  );
  const [selectedId, setSelectedId] = useState<string>();
  const [draft, setDraft] = useState<LabelEditorDraft>();
  const [baseline, setBaseline] = useState('');
  const [creating, setCreating] = useState(false);
  const [mutation, setMutation] = useState<MutationKind>();
  const [mutationError, setMutationError] = useState('');
  const [notice, setNotice] = useState('');
  const dirty = draft !== undefined && serializeLabelDraft(draft) !== baseline;
  const canCreate = !readOnly && Boolean(onCreate);
  const canUpdate = !readOnly && Boolean(onUpdate);
  const canDelete = !readOnly && Boolean(onDelete && confirmDelete);
  const busy = mutation !== undefined;

  useEffect(() => {
    if (creating || busy || dirty) return;
    const selected = effectiveLabels.find((item) => item.id === selectedId);
    if (selected) {
      if (!draft || draft.id !== selected.id || draft.version !== selected.version) {
        loadLabelDraft(selected, setDraft, setBaseline);
      }
      return;
    }
    const first = effectiveLabels[0];
    if (first) {
      setSelectedId(first.id);
      loadLabelDraft(first, setDraft, setBaseline);
    } else if (!creating) {
      setSelectedId(undefined);
      setDraft(undefined);
      setBaseline('');
    }
  }, [busy, creating, dirty, draft, effectiveLabels, selectedId]);

  const selectLabel = async (label: WorkflowApprovalLabel) => {
    if (label.id === selectedId && !creating) return;
    if (!await changesCanBeDiscarded(dirty, draft?.name, confirmDiscardChanges)) return;
    setCreating(false);
    setSelectedId(label.id);
    setMutationError('');
    setNotice('');
    loadLabelDraft(label, setDraft, setBaseline);
  };

  const startCreate = async () => {
    if (!canCreate || !await changesCanBeDiscarded(dirty, draft?.name, confirmDiscardChanges)) return;
    const next = newLabelDraft();
    setCreating(true);
    setSelectedId(undefined);
    setDraft(next);
    setBaseline(serializeLabelDraft(next));
    setMutationError('');
    setNotice('');
  };

  const resetDraft = () => {
    if (!draft || busy) return;
    if (creating) {
      const next = newLabelDraft();
      setDraft(next);
      setBaseline(serializeLabelDraft(next));
      return;
    }
    const source = effectiveLabels.find((item) => item.id === draft.id);
    if (source) loadLabelDraft(source, setDraft, setBaseline);
  };

  const saveLabel = async () => {
    if (!draft || busy) return;
    const validationError = labelValidationError(draft);
    if (validationError) {
      setMutationError(validationError);
      return;
    }
    const input: WorkflowApprovalLabelInput = {
      name: draft.name.trim(),
      description: optionalTrimmed(draft.description),
      tone: draft.tone,
      expectedVersion: draft.id ? draft.version : undefined,
    };
    setMutation(draft.id ? 'update' : 'create');
    setMutationError('');
    setNotice('');
    try {
      const saved = draft.id
        ? await requireCallback(onUpdate, '승인 라벨 수정 기능이 연결되지 않았습니다.')(draft.id, input)
        : await requireCallback(onCreate, '승인 라벨 생성 기능이 연결되지 않았습니다.')(input);
      setConfirmedLabels((current) => ({ ...current, [saved.id]: saved }));
      setConfirmedDeletedIds((current) => withoutSetValue(current, saved.id));
      setCreating(false);
      setSelectedId(saved.id);
      loadLabelDraft(saved, setDraft, setBaseline);
      setNotice(`“${saved.name}” 승인 라벨을 저장했습니다.`);
    } catch (reason) {
      setMutationError(errorMessage(reason, '승인 라벨을 저장하지 못했습니다.'));
    } finally {
      setMutation(undefined);
    }
  };

  const deleteLabel = async () => {
    if (!draft?.id || draft.version === undefined || !onDelete || !confirmDelete || busy) return;
    const target: WorkflowApprovalSettingsDeleteTarget = {
      kind: 'label', id: draft.id, name: draft.name, version: draft.version,
    };
    if (!await confirmDelete(target)) return;
    setMutation('delete');
    setMutationError('');
    setNotice('');
    try {
      await onDelete(target.id, target.version);
      const remaining = effectiveLabels.filter((item) => item.id !== target.id);
      setConfirmedDeletedIds((current) => withSetValue(current, target.id));
      setConfirmedLabels((current) => omitRecordKey(current, target.id));
      setCreating(false);
      if (remaining[0]) {
        setSelectedId(remaining[0].id);
        loadLabelDraft(remaining[0], setDraft, setBaseline);
      } else {
        setSelectedId(undefined);
        setDraft(undefined);
        setBaseline('');
      }
      setNotice(`“${target.name}” 승인 라벨을 보관했습니다.`);
    } catch (reason) {
      setMutationError(errorMessage(reason, '승인 라벨을 보관하지 못했습니다. 다른 설정에서 사용 중인지 확인해 주세요.'));
    } finally {
      setMutation(undefined);
    }
  };

  const updateDraft = (patch: Partial<LabelEditorDraft>) => {
    setDraft((current) => current ? { ...current, ...patch } : current);
    setMutationError('');
    setNotice('');
  };

  return <ResourceState
    loading={loading}
    error={error}
    empty={false}
    onRetry={onRetry}
    loadingLabel="승인 라벨을 불러오는 중입니다."
  >
    <Stack spacing={1.5}>
      {readOnly ? <Alert severity="info">조회 전용 상태입니다. 승인 라벨을 확인할 수 있지만 변경할 수는 없습니다.</Alert> : null}
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice('')} />
      {mutationError ? <Alert severity="error" onClose={() => setMutationError('')}>{mutationError}</Alert> : null}
      <SettingsSplitLayout
        list={<SettingsListCard
          title="커스텀 승인 라벨"
          description="기본 ‘승인’ 라벨은 항상 제공됩니다. 커스텀 라벨은 단계의 의미와 표시만 바꾸며 승인 순서나 권한은 바꾸지 않습니다."
          action={canCreate ? <Button size="small" startIcon={<AddOutlined />} onClick={() => void startCreate()} disabled={busy}>새 라벨</Button> : undefined}
          empty={effectiveLabels.length === 0}
          emptyMessage={readOnly ? '등록된 커스텀 승인 라벨이 없습니다.' : '등록된 라벨이 없습니다. 첫 라벨을 만들어 보세요.'}
        >
          {effectiveLabels.map((label) => <SettingsListButton
            key={label.id}
            selected={!creating && selectedId === label.id}
            ariaLabel={`${label.name} 승인 라벨 편집`}
            onClick={() => void selectLabel(label)}
            disabled={busy}
            title={<ApprovalLabelBadge name={label.name} tone={label.tone} />}
            description={label.description || '설명이 없습니다.'}
            meta={`v${label.version}`}
          />)}
        </SettingsListCard>}
        editor={draft ? <Box component="section" aria-labelledby={editorTitleId} sx={{ minWidth: 0 }}>
          <SectionCard
            title={<Typography id={editorTitleId} component="h3" variant="h6">{creating ? '새 승인 라벨' : '승인 라벨 편집'}</Typography>}
            bodyPadding="standard"
            action={readOnly ? undefined : <EditorActions
              dirty={dirty}
              busy={busy}
              saving={mutation === 'create' || mutation === 'update'}
              canSave={creating ? canCreate : canUpdate}
              onReset={resetDraft}
              onSave={() => void saveLabel()}
            />}
          >
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary">{draft.version ? `버전 ${draft.version}` : '저장 전'}</Typography>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>미리보기</Typography>
                <ApprovalLabelBadge name={draft.name.trim() || '승인 라벨'} tone={draft.tone} />
              </Box>
              <FormTextField
                required
                label="라벨 이름"
                value={draft.name}
                disabled={readOnly || busy}
                onChange={(event) => updateDraft({ name: event.target.value })}
                characterCount={{ current: draft.name.length, max: 40 }}
                slotProps={{ htmlInput: { maxLength: 40 } }}
              />
              <FormTextField
                label="설명"
                value={draft.description ?? ''}
                disabled={readOnly || busy}
                onChange={(event) => updateDraft({ description: event.target.value })}
                multiline
                minRows={3}
                characterCount={{ current: (draft.description ?? '').length, max: 200 }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <FormTextField
                select
                required
                label="라벨 색상"
                value={draft.tone}
                disabled={readOnly || busy}
                onChange={(event) => updateDraft({ tone: event.target.value as WorkflowApprovalLabelTone })}
              >
                {Object.entries(workflowApprovalLabelToneLabel).map(([tone, label]) => <MenuItem key={tone} value={tone}>
                  <ValueBadge size="compact" label={label} tone={workflowApprovalLabelBadgeTone[tone as WorkflowApprovalLabelTone]} />
                </MenuItem>)}
              </FormTextField>
              {!creating && canDelete && draft.id ? <>
                <Divider />
                <EditorArchiveAction busy={busy} deleting={mutation === 'delete'} onDelete={() => void deleteLabel()} />
              </> : null}
            </Stack>
          </SectionCard>
        </Box> : <EditorEmptyState
          title="승인 라벨을 선택하세요."
          description={readOnly ? '왼쪽 목록에서 확인할 라벨을 선택하세요.' : '기존 라벨을 선택하거나 새 라벨을 만드세요.'}
          action={canCreate ? <Button variant="contained" startIcon={<AddOutlined />} onClick={() => void startCreate()}>새 라벨</Button> : undefined}
        />}
      />
    </Stack>
  </ResourceState>;
}

function SettingsSplitLayout({ list, editor }: { list: ReactNode; editor: ReactNode }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(240px, 0.72fr) minmax(0, 1.55fr)' }, gap: 2, alignItems: 'start' }}>
    {list}
    {editor}
  </Box>;
}

function SettingsListCard({
  title,
  description,
  action,
  empty,
  emptyMessage,
  children,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  empty: boolean;
  emptyMessage: string;
  children: ReactNode;
}) {
  return <SectionCard title={title} help={description} action={action}>
    {empty ? <EmptyState>{emptyMessage}</EmptyState> : <Stack divider={<Divider />}>{children}</Stack>}
  </SectionCard>;
}

function SettingsListButton({
  selected,
  ariaLabel,
  onClick,
  disabled,
  title,
  description,
  meta,
}: {
  selected: boolean;
  ariaLabel: string;
  onClick: () => void;
  disabled: boolean;
  title: ReactNode;
  description: string;
  meta: string;
}) {
  return <ButtonBase
    aria-label={ariaLabel}
    aria-current={selected ? 'true' : undefined}
    onClick={onClick}
    disabled={disabled}
    sx={{
      width: '100%',
      minWidth: 0,
      px: 1.75,
      py: 1.35,
      textAlign: 'left',
      alignItems: 'stretch',
      bgcolor: selected ? designTokens.color.compactSelectedSurface : designTokens.color.canvas,
      '&:hover': { bgcolor: selected ? designTokens.color.selectedNavigationSurface : designTokens.color.hoverSurface },
      '&:focus-visible': createFocusRingStyles({ inset: true }),
      '&.Mui-disabled': { opacity: 0.62 },
    }}
  >
    <Stack spacing={0.4} sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
        <Typography component="div" variant="body2" sx={{ minWidth: 0, ...designTokens.typography.subsectionTitle }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{meta}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</Typography>
    </Stack>
  </ButtonBase>;
}

function EditorActions({
  dirty,
  busy,
  saving,
  canSave,
  onReset,
  onSave,
}: {
  dirty: boolean;
  busy: boolean;
  saving: boolean;
  canSave: boolean;
  onReset: () => void;
  onSave: () => void;
}) {
  return <Stack direction="row" spacing={1} role="group" aria-label="편집 작업">
    <Button variant="outlined" color="inherit" onClick={onReset} disabled={busy || !dirty}>취소</Button>
    {canSave ? <Button variant="outlined" onClick={onSave} disabled={busy || !dirty}>{saving ? '저장 중…' : '저장'}</Button> : null}
  </Stack>;
}

function EditorArchiveAction({ busy, deleting, onDelete }: { busy: boolean; deleting: boolean; onDelete: () => void }) {
  return <Box>
    <Button color="error" startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineOutlined />} onClick={onDelete} disabled={busy}>
      {deleting ? '보관 중…' : '보관'}
    </Button>
  </Box>;
}

function EditorEmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <Paper variant="outlined" sx={{ minHeight: 260, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, p: 3 }}>
    <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.control, bgcolor: designTokens.color.actionSoft, color: designTokens.color.action }}><EditOutlined /></Box>
      <Typography variant="body2" sx={designTokens.typography.subsectionTitle}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
      {action ? <Box sx={{ pt: 0.5 }}>{action}</Box> : null}
    </Stack>
  </Paper>;
}

function ApprovalStepsEditor({
  value,
  labels,
  disabled,
  renderFixedPersonPicker,
  renderFixedRolePicker,
  onChange,
}: {
  value: WorkflowApprovalTemplateStep[];
  labels: readonly WorkflowApprovalLabel[];
  disabled: boolean;
  renderFixedPersonPicker?: ApprovalTemplatesPanelProps['renderFixedPersonPicker'];
  renderFixedRolePicker?: ApprovalTemplatesPanelProps['renderFixedRolePicker'];
  onChange: (value: WorkflowApprovalTemplateStep[]) => void;
}) {
  const updateStep = (index: number, patch: Partial<WorkflowApprovalTemplateStep>) => {
    onChange(value.map((step, current) => current === index ? { ...step, ...patch } : step));
  };
  const moveStep = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= value.length || value[target].stageOrder !== value[index].stageOrder) return;
    const next = value.map(cloneStep);
    [next[index], next[target]] = [next[target], next[index]];
    onChange(reorderSteps(next));
  };
  const removeStep = (index: number) => onChange(reorderSteps(value.filter((_, current) => current !== index)));
  const addStage = () => {
    if (value.length >= 20) return;
    onChange(reorderSteps([...value, newApprovalStep(value.length + 1)]));
  };
  const addStepToStage = (stageOrder: number) => {
    if (value.length >= 20) return;
    onChange(reorderSteps(insertWorkflowApprovalStepInStage(
      value,
      stageOrder,
      newApprovalStep(value.length + 1),
    )));
  };
  const mergeStageWithPrevious = (stageOrder: number) => {
    onChange(reorderSteps(mergeWorkflowApprovalStageWithPrevious(value, stageOrder)));
  };
  const splitStageAt = (index: number) => {
    onChange(reorderSteps(splitWorkflowApprovalStageAt(value, index)));
  };
  const stageGroups = groupWorkflowApprovalSteps(value);
  const moveStage = (stageOrder: number, direction: -1 | 1) => {
    onChange(reorderSteps(moveWorkflowApprovalStage(value, stageOrder, direction)));
  };

  return <SectionCard
    title={<Typography component="h4" sx={designTokens.typography.subsectionTitle}>승인 단계</Typography>}
    action={<Button size="small" startIcon={<AddOutlined />} onClick={addStage} disabled={disabled || value.length >= 20}>다음 단계 추가</Button>}
  >
    {value.length === 0 ? <EmptyState>승인 단계를 하나 이상 추가하세요.</EmptyState> : <Stack spacing={1.5} sx={{ p: { xs: 1.25, sm: 1.5 } }}>
      {stageGroups.map((stage) => <Box
        component="section"
        key={`approval-template-stage-${stage.stageOrder}`}
        data-testid={`workflow-approval-stage-group-${stage.stageOrder}`}
        aria-label={`${stage.stageOrder}단계 승인자 ${stage.items.length}명`}
        sx={{ overflow: 'hidden', border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.canvas }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ px: 1.5, py: 1.25, alignItems: { sm: 'center' }, justifyContent: 'space-between', bgcolor: designTokens.color.sidebarSurface, borderBottom: `1px solid ${designTokens.color.divider}` }}
        >
          <Stack direction="row" spacing={1} sx={{ minWidth: 0, alignItems: 'center' }}>
            <Box sx={{ width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: designTokens.color.actionSoft, color: designTokens.color.actionDark, fontSize: designTokens.typography.supporting.fontSize, fontWeight: 800 }}>{stage.stageOrder}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={designTokens.typography.subsectionTitle}>{stage.stageOrder}단계</Typography>
                {stage.items.length > 1 ? <ValueBadge label="병렬 승인" /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {stage.items.length > 1 ? `승인자 ${stage.items.length}명 · 모두 승인하면 다음 단계로 진행` : '승인자 1명'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <IconButton size="small" aria-label={`${stage.stageOrder}단계 위로 이동`} disabled={disabled || stage.stageOrder === 1} onClick={() => moveStage(stage.stageOrder, -1)}><ArrowUpwardOutlined fontSize="small" /></IconButton>
            <IconButton size="small" aria-label={`${stage.stageOrder}단계 아래로 이동`} disabled={disabled || stage.stageOrder === stageGroups.length} onClick={() => moveStage(stage.stageOrder, 1)}><ArrowDownwardOutlined fontSize="small" /></IconButton>
            {stage.stageOrder > 1 ? <Button size="small" variant="text" disabled={disabled} onClick={() => mergeStageWithPrevious(stage.stageOrder)}>{stage.stageOrder}단계를 이전 단계와 합치기</Button> : null}
            <Button size="small" variant="outlined" startIcon={<AddOutlined />} disabled={disabled || value.length >= 20} onClick={() => addStepToStage(stage.stageOrder)}>같은 단계 승인자 추가</Button>
          </Stack>
        </Stack>
        <Stack divider={<Divider />}>
          {stage.items.map(({ step, index }, memberIndex) => {
            const label = labels.find((item) => item.id === step.approvalLabelId) ?? step.approvalLabel;
            return <Box key={step.id ?? `approval-template-step-${index}`} sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1.25, minWidth: 0, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', ...designTokens.typography.subsectionTitle }}>{step.label.trim() || `승인자 ${memberIndex + 1}`}</Typography>
            <ApprovalLabelBadge name={label?.name ?? '승인'} tone={label?.tone ?? 'GREEN'} compact />
            {memberIndex > 0 ? <Button size="small" onClick={() => splitStageAt(index)} disabled={disabled}>이 승인자부터 다음 단계로 분리</Button> : null}
            <Stack direction="row" spacing={0.25}>
              <IconButton size="small" aria-label={`${step.label} 같은 단계에서 위로 이동`} onClick={() => moveStep(index, -1)} disabled={disabled || memberIndex === 0}><ArrowUpwardOutlined fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`${step.label} 같은 단계에서 아래로 이동`} onClick={() => moveStep(index, 1)} disabled={disabled || memberIndex === stage.items.length - 1}><ArrowDownwardOutlined fontSize="small" /></IconButton>
              <IconButton size="small" color="error" aria-label={`${step.label} 삭제`} onClick={() => removeStep(index)} disabled={disabled}><DeleteOutlineOutlined fontSize="small" /></IconButton>
            </Stack>
          </Stack>
          <Stack spacing={1.25}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1.4fr) minmax(150px, 0.8fr)' }, gap: 1.25 }}>
              <FormTextField
                required
                label="단계 이름"
                value={step.label}
                disabled={disabled}
                onChange={(event) => updateStep(index, { label: event.target.value })}
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <FormTextField
                select
                label="승인 라벨"
                value={step.approvalLabelId ?? ''}
                disabled={disabled}
                onChange={(event) => updateStep(index, { approvalLabelId: event.target.value || undefined, approvalLabel: undefined })}
              >
                <MenuItem value=""><ApprovalLabelBadge name="승인" tone="GREEN" compact /></MenuItem>
                {labels.map((item) => <MenuItem key={item.id} value={item.id}><ApprovalLabelBadge name={item.name} tone={item.tone} compact /></MenuItem>)}
              </FormTextField>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(180px, 1fr) minmax(0, 1fr)' }, gap: 1.25 }}>
              <FormTextField
                select
                required
                label="승인자"
                value={step.resolverType}
                disabled={disabled}
                onChange={(event) => updateStep(index, {
                  resolverType: event.target.value as ReusableApprovalResolverType,
                  resolverConfig: defaultResolverConfig(event.target.value as ReusableApprovalResolverType, index + 1),
                })}
              >
                {Object.entries(resolverPresentation).map(([resolverType, name]) => <MenuItem key={resolverType} value={resolverType}><ValueBadge label={name} /></MenuItem>)}
              </FormTextField>
              <ResolverConfiguration
                step={step}
                disabled={disabled}
                renderFixedPersonPicker={renderFixedPersonPicker}
                renderFixedRolePicker={renderFixedRolePicker}
                onChange={(resolverConfig) => updateStep(index, { resolverConfig })}
              />
            </Box>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}><Typography variant="body2">{step.label || `${step.order}번째 승인자`} 세부 설정</Typography></AccordionSummary>
              <AccordionDetails><Stack spacing={1.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
              <FormTextField select label="승인자를 못 찾으면" value={step.missingPolicy} disabled={disabled} onChange={(event) => updateStep(index, { missingPolicy: event.target.value as WorkflowApprovalTemplateStep['missingPolicy'] })}>
                <MenuItem value="FAIL"><ValueBadge label="제출 차단" /></MenuItem>
                <MenuItem value="SKIP"><ValueBadge label="단계 생략" /></MenuItem>
                <MenuItem value="FALLBACK_ROOT_ORG_LEADER"><ValueBadge label="최상위 조직장으로 대체" /></MenuItem>
              </FormTextField>
              <FormTextField select label="본인이 승인자면" value={step.selfPolicy} disabled={disabled} onChange={(event) => updateStep(index, { selfPolicy: event.target.value as WorkflowApprovalTemplateStep['selfPolicy'] })}>
                <MenuItem value="SKIP"><ValueBadge label="단계 생략" /></MenuItem>
                <MenuItem value="FAIL"><ValueBadge label="제출 차단" /></MenuItem>
              </FormTextField>
              <FormTextField select label="앞 단계와 같으면" value={step.duplicatePolicy} disabled={disabled} onChange={(event) => updateStep(index, { duplicatePolicy: event.target.value as WorkflowApprovalTemplateStep['duplicatePolicy'] })}>
                <MenuItem value="SKIP"><ValueBadge label="중복 생략" /></MenuItem>
                <MenuItem value="KEEP"><ValueBadge label="그대로 유지" /></MenuItem>
              </FormTextField>
              <WorkflowDurationField
                label="처리 기한"
                value={step.dueAfterMinutes === undefined ? '' : String(step.dueAfterMinutes)}
                disabled={disabled}
                onChange={(minutes) => updateStep(index, { dueAfterMinutes: minutes ? Number(minutes) : undefined })}
                helperText="비워두면 별도 기한을 정하지 않습니다."
              />
            </Box>
            <ChoiceControl
              kind="checkbox"
              checked={step.requesterEditable}
              disabled={disabled}
              onCheckedChange={(checked) => updateStep(index, { requesterEditable: checked })}
              label="요청자가 이 단계를 변경할 수 있음"
              sx={{ alignItems: 'flex-start' }}
            />
              </Stack></AccordionDetails>
            </Accordion>
          </Stack>
        </Box>;
          })}
        </Stack>
      </Box>)}
    </Stack>}
  </SectionCard>;
}

function ResolverConfiguration({
  step,
  disabled,
  renderFixedPersonPicker,
  renderFixedRolePicker,
  onChange,
}: {
  step: WorkflowApprovalTemplateStep;
  disabled: boolean;
  renderFixedPersonPicker?: ApprovalTemplatesPanelProps['renderFixedPersonPicker'];
  renderFixedRolePicker?: ApprovalTemplatesPanelProps['renderFixedRolePicker'];
  onChange: (config: Record<string, unknown>) => void;
}) {
  if (step.resolverType === 'REQUESTER_ORG_LEADER') {
    return <FormTextField
      type="number"
      required
      label="조직장 단계"
      value={numberValue(step.resolverConfig?.level, 1)}
      disabled={disabled}
      onChange={(event) => onChange({ level: Math.max(1, Number(event.target.value)) })}
      slotProps={{ htmlInput: { min: 1, max: 20, step: 1 } }}
    />;
  }
  if (step.resolverType === 'FIXED_PERSON') {
    return renderFixedPersonPicker?.({
      personID: stringValue(step.resolverConfig?.personId),
      disabled,
      onChange: (personId) => onChange({ personId }),
    }) ?? <Alert severity="warning">구성원 검색을 불러오지 못했습니다.</Alert>;
  }
  if (step.resolverType === 'FIXED_ROLE') {
    return renderFixedRolePicker?.({
      roleCode: stringValue(step.resolverConfig?.roleCode),
      disabled,
      onChange: (roleCode) => onChange({ roleCode }),
    }) ?? <Alert severity="warning">승인 역할 검색을 불러오지 못했습니다.</Alert>;
  }
  return <FormTextField select label="승인자 기준" value="ROOT_ORG_LEADER" disabled><MenuItem value="ROOT_ORG_LEADER"><ValueBadge label="공식 최상위 조직장" /></MenuItem></FormTextField>;
}

function ApprovalLabelBadge({ name, tone, compact = false }: { name: string; tone: WorkflowApprovalLabelTone; compact?: boolean }) {
  return <ValueBadge label={name} tone={workflowApprovalLabelBadgeTone[tone]} size={compact ? 'compact' : 'small'} />;
}

function newApprovalStep(order: number): WorkflowApprovalTemplateStep {
  return {
    order,
    stageOrder: order,
    label: `${order}차 승인`,
    resolverType: 'REQUESTER_ORG_LEADER',
    resolverConfig: { level: order },
    missingPolicy: 'FAIL',
    selfPolicy: 'SKIP',
    duplicatePolicy: 'SKIP',
    requesterEditable: true,
  };
}

function newTemplateDraft(): TemplateEditorDraft {
  return { name: '', description: '', approvalSteps: [newApprovalStep(1)] };
}

function newLabelDraft(): LabelEditorDraft {
  return { name: '', description: '', tone: 'GREEN' };
}

function templateToDraft(template: WorkflowApprovalTemplate): TemplateEditorDraft {
  return {
    id: template.id,
    version: template.version,
    name: template.name,
    description: template.description ?? '',
    approvalSteps: reorderSteps(template.approvalSteps.map(cloneStep)),
  };
}

function labelToDraft(label: WorkflowApprovalLabel): LabelEditorDraft {
  return {
    id: label.id,
    version: label.version,
    name: label.name,
    description: label.description ?? '',
    tone: label.tone,
  };
}

function loadTemplateDraft(
  template: WorkflowApprovalTemplate,
  setDraft: (value: TemplateEditorDraft) => void,
  setBaseline: (value: string) => void,
) {
  const next = templateToDraft(template);
  setDraft(next);
  setBaseline(serializeTemplateDraft(next));
}

function loadLabelDraft(
  label: WorkflowApprovalLabel,
  setDraft: (value: LabelEditorDraft) => void,
  setBaseline: (value: string) => void,
) {
  const next = labelToDraft(label);
  setDraft(next);
  setBaseline(serializeLabelDraft(next));
}

function serializeTemplateDraft(draft: TemplateEditorDraft): string {
  return JSON.stringify({
    name: draft.name,
    description: draft.description ?? '',
    approvalSteps: reorderSteps(draft.approvalSteps).map((step) => ({
      order: step.order,
      stageOrder: step.stageOrder,
      label: step.label,
      approvalLabelId: step.approvalLabelId ?? '',
      resolverType: step.resolverType,
      resolverConfig: step.resolverConfig ?? {},
      missingPolicy: step.missingPolicy,
      selfPolicy: step.selfPolicy,
      duplicatePolicy: step.duplicatePolicy,
      dueAfterMinutes: step.dueAfterMinutes ?? null,
      requesterEditable: step.requesterEditable,
    })),
  });
}

function serializeLabelDraft(draft: LabelEditorDraft): string {
  return JSON.stringify({ name: draft.name, description: draft.description ?? '', tone: draft.tone });
}

function reorderSteps(steps: readonly WorkflowApprovalTemplateStep[]): WorkflowApprovalTemplateStep[] {
  return normalizeWorkflowApprovalSteps(steps);
}

function cloneStep(step: WorkflowApprovalTemplateStep): WorkflowApprovalTemplateStep {
  return { ...step, resolverConfig: { ...(step.resolverConfig ?? {}) }, approvalLabel: step.approvalLabel ? { ...step.approvalLabel } : undefined };
}

function defaultResolverConfig(resolverType: ReusableApprovalResolverType, order: number): Record<string, unknown> {
  if (resolverType === 'REQUESTER_ORG_LEADER') return { level: order };
  if (resolverType === 'FIXED_PERSON') return { personId: '' };
  if (resolverType === 'FIXED_ROLE') return { roleCode: '' };
  return {};
}

function templateValidationError(draft: TemplateEditorDraft): string {
  const name = draft.name.trim();
  const description = draft.description?.trim() ?? '';
  if (!name) return '템플릿 이름을 입력하세요.';
  if (name.length > 120) return '템플릿 이름은 120자 이내로 입력하세요.';
  if (description.length > 500) return '템플릿 설명은 500자 이내로 입력하세요.';
  if (draft.approvalSteps.length === 0) return '승인 단계를 하나 이상 추가하세요.';
  if (draft.approvalSteps.length > 20) return '승인 단계는 최대 20개까지 추가할 수 있습니다.';
  if (!workflowApprovalStageSequenceIsValid(draft.approvalSteps)) return '병렬 승인 단계 구성을 확인하세요.';
  for (const [index, step] of draft.approvalSteps.entries()) {
    if (!step.label.trim()) return `${index + 1}단계 이름을 입력하세요.`;
    if (step.label.trim().length > 120) return `${index + 1}단계 이름은 120자 이내로 입력하세요.`;
    if (step.resolverType === 'SUBJECT_ORG_LEADER' || step.resolverType === 'FORM_PERSON_FIELD') return `${index + 1}단계는 워크플로우 양식 필드에 의존해 승인 템플릿에서 사용할 수 없습니다.`;
    if (step.resolverType === 'REQUESTER_ORG_LEADER' && numberValue(step.resolverConfig?.level, 0) < 1) return `${index + 1}단계의 조직장 단계를 확인하세요.`;
    if (step.resolverType === 'FIXED_PERSON' && !stringValue(step.resolverConfig?.personId).trim()) return `${index + 1}단계의 구성원을 선택하세요.`;
    if (step.resolverType === 'FIXED_ROLE' && !stringValue(step.resolverConfig?.roleCode).trim()) return `${index + 1}단계의 승인 역할을 선택하세요.`;
    if (step.dueAfterMinutes !== undefined && (
      !Number.isInteger(step.dueAfterMinutes)
      || step.dueAfterMinutes <= 0
      || step.dueAfterMinutes > 2_147_483_647
    )) return `${index + 1}단계의 처리 기한은 1 이상 2,147,483,647 이하의 정수로 입력하세요.`;
  }
  return '';
}

function labelValidationError(draft: LabelEditorDraft): string {
  const name = draft.name.trim();
  const description = draft.description?.trim() ?? '';
  if (!name) return '라벨 이름을 입력하세요.';
  if (name === '승인') return '기본 ‘승인’ 라벨은 별도로 제공됩니다. 다른 라벨 이름을 입력하세요.';
  if (name.length > 40) return '라벨 이름은 40자 이내로 입력하세요.';
  if (description.length > 200) return '라벨 설명은 200자 이내로 입력하세요.';
  return '';
}

async function changesCanBeDiscarded(
  dirty: boolean,
  name: string | undefined,
  confirmDiscardChanges: ((name: string) => boolean | Promise<boolean>) | undefined,
): Promise<boolean> {
  if (!dirty || !confirmDiscardChanges) return true;
  return confirmDiscardChanges(name?.trim() || '새 항목');
}

function optionalTrimmed(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

function requireCallback<Arguments extends unknown[], Result>(
  callback: ((...args: Arguments) => Promise<Result>) | undefined,
  message: string,
): (...args: Arguments) => Promise<Result> {
  if (!callback) throw new Error(message);
  return callback;
}

function mergeConfirmedRecords<RecordType extends { id: string }>(
  source: readonly RecordType[],
  confirmed: Readonly<Record<string, RecordType>>,
  deletedIds: ReadonlySet<string>,
): RecordType[] {
  const result = source
    .filter((item) => !deletedIds.has(item.id))
    .map((item) => confirmed[item.id] ?? item);
  const sourceIds = new Set(result.map((item) => item.id));
  for (const item of Object.values(confirmed)) {
    if (!deletedIds.has(item.id) && !sourceIds.has(item.id)) result.push(item);
  }
  return result;
}

function withSetValue(source: ReadonlySet<string>, value: string): ReadonlySet<string> {
  const next = new Set(source);
  next.add(value);
  return next;
}

function withoutSetValue(source: ReadonlySet<string>, value: string): ReadonlySet<string> {
  const next = new Set(source);
  next.delete(value);
  return next;
}

function omitRecordKey<RecordType>(source: Readonly<Record<string, RecordType>>, key: string): Record<string, RecordType> {
  const next = { ...source };
  delete next[key];
  return next;
}
